import type { ReactNode } from "react"
import Icon from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"
import Link from "@/v2-backoffice-client/components/link"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import {
  describeCarrierScope,
  describeFreeSource,
  type CustomerFeatureAccessSource,
} from "@/v2-backoffice-client/customer/featureAccess/customer.featureAccess.utils"
import type { LicenseSeatHolder } from "@/v2-backoffice-client/license/seats/license.seats.utils"
import type { CustomerFeatureAccessFragment } from "@/v2-backoffice-client/customer/featureAccess/__types__/customer.featureAccess.fragment"

export type UserFeatureAccessHeadlineProps = {
  userId: number
  /** The person, as the sentences name them. */
  userName: string
  access: CustomerFeatureAccessFragment
  customerName: string
  /** Keys the customer panel the rows link to; null when the owner has none. */
  externalCustomerId: string | null | undefined
  /**
   * Seats held across the customer's seated licenses; says which this
   * person holds. Undefined while the read is in flight: a limited carrier
   * then says nothing about them rather than "holds no seat".
   */
  seatHolders: readonly LicenseSeatHolder[] | undefined
}

type Source = Omit<CustomerFeatureAccessSource, "key" | "text"> & {
  key: string
  text: ReactNode
}

/** The customer panel from inside this one: pushed onto the trail, so the breadcrumb leads back. */
function customerLink(
  externalCustomerId: string | null | undefined,
  tab: Parameters<typeof buildCustomerViewLink>[1],
) {
  return externalCustomerId
    ? `>>${buildCustomerViewLink(externalCustomerId, tab)}`
    : null
}

// What this person can use, said before any list: the same three sources
// the customer's tab names, read for one person. Free for everyone is the
// customer's answer and so is the own list; the licenses are where one
// person can differ, so every seated carrier says whether this person holds
// a seat on it. Each source says where it is changed, which is never here:
// a person's access is their customer's, set on the customer panel.
function UserFeatureAccessHeadline(props: UserFeatureAccessHeadlineProps) {
  const {
    userId,
    userName,
    access,
    customerName,
    externalCustomerId,
    seatHolders,
  } = props

  const free = describeFreeSource(access)
  const hasLicense = access.LiveLicenseCount + access.LiveSeatedLicenseCount > 0
  const picks = access.OwnList?.FeatureCount ?? 0
  const rules = access.OwnList?.RuleCount ?? 0
  const licensesTab = customerLink(externalCustomerId, "billing")
  const accessTab = customerLink(externalCustomerId, "feature-access")

  /** Whether this person holds a seat on the license; null until the holders are read. */
  function holdsSeatOn(licenseId: number) {
    if (seatHolders === undefined) return null
    return seatHolders.some(
      (holder) => holder.LicenseId === licenseId && holder.UserId === userId,
    )
  }

  const sources: Source[] = [
    { ...free },
    {
      key: "licenses",
      label: `${customerName}'s licenses`,
      applies:
        access.Carriers.length > 0 &&
        access.Carriers.some(
          (carrier) =>
            !carrier.SeatHoldersOnly ||
            holdsSeatOn(carrier.LicenseId) !== false,
        ),
      text:
        access.Carriers.length === 0 ? (
          hasLicense ? (
            `${customerName}'s live licenses include no packages, so nothing package-only reaches ${userName} through a license or add-on.`
          ) : (
            `${customerName} holds no live license. Nothing from a license or add-on reaches ${userName}.`
          )
        ) : (
          // One carrier per line; a seated carrier says whether this
          // person is on it, which is the one thing that is theirs alone.
          <ul className="flex flex-col gap-1">
            {access.Carriers.map((carrier) => {
              const holds = carrier.SeatHoldersOnly
                ? holdsSeatOn(carrier.LicenseId)
                : null
              return (
                <li
                  key={`${carrier.Kind}:${carrier.LicenseId}:${carrier.Name}`}
                >
                  <span className="text-foreground">{carrier.Name}</span>
                  {" — "}
                  {describeCarrierScope(carrier)}
                  {holds !== null && (
                    <div
                      className={cn(
                        "mt-0.5 border-l-2 pl-2",
                        holds ? "border-primary/60" : "border-border",
                      )}
                    >
                      {holds
                        ? `${userName} holds a seat on it.`
                        : `${userName} holds no seat on it, so it does not reach them. Seats are named on the customer panel's Licenses & add-ons tab (row menu, Seats).`}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        ),
    },
    {
      key: "ownList",
      label: `Added for ${customerName}`,
      applies: picks + rules > 0 && access.LiveLicenseCount > 0,
      text:
        picks + rules === 0
          ? `Nothing yet. The customer panel's What they get tab can add features for ${customerName} only.`
          : `${[
              picks > 0 ? `${picks} ticked` : null,
              rules > 0
                ? `${rules} header ${rules === 1 ? "rule" : "rules"}`
                : null,
            ]
              .filter(Boolean)
              .join(", ")} on ${customerName}'s own list${
              access.LiveLicenseCount === 0
                ? ", reaching nobody until a license is live"
                : ""
            }.`,
    },
  ]

  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 pb-1 pt-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-2.5">
        <h3 className="text-sm font-medium">What {userName} can use</h3>
        <span className="text-xs text-muted-foreground">
          Decided for{" "}
          {externalCustomerId ? (
            <Link
              variant="muted"
              size="xs"
              iconSrc={null}
              to={`>>${buildCustomerViewLink(externalCustomerId)}`}
              className="underline underline-offset-2"
            >
              {customerName}
            </Link>
          ) : (
            customerName
          )}
          , the customer that owns this user; read here, changed there.
        </span>
      </div>
      <dl className="divide-y divide-border/70 border-t border-border/70 text-xs">
        {sources.map((source) => (
          <div
            key={source.key}
            className="grid grid-cols-[9rem_1fr] gap-x-4 py-2.5"
          >
            <dt
              className={cn(
                "flex items-start gap-1.5 font-medium",
                !source.applies && "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "mt-px flex size-3.5 shrink-0 items-center justify-center",
                  source.applies ? "text-primary" : "text-muted-foreground",
                )}
                aria-hidden
              >
                <Icon src={source.applies ? "check" : "cross"} size={11} />
              </span>
              {source.label}
            </dt>
            <dd className="min-w-0 text-muted-foreground">
              {source.text}
              {source.key === "licenses" && licensesTab && (
                <Link
                  variant="muted"
                  size="xs"
                  iconSrc={null}
                  to={licensesTab}
                  className="mt-1 underline underline-offset-2"
                >
                  Licenses, add-ons and seats: the customer panel&apos;s
                  Licenses &amp; add-ons tab
                </Link>
              )}
              {source.key === "ownList" && accessTab && (
                <Link
                  variant="muted"
                  size="xs"
                  iconSrc={null}
                  to={accessTab}
                  className="mt-1 underline underline-offset-2"
                >
                  The list and the open or listed classes: the customer
                  panel&apos;s What they get tab
                </Link>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

export default UserFeatureAccessHeadline
